import type { AttachmentFit, TowerKit } from "../kit/types";
import type { FitSpec } from "../kit/bigTypes";
import { ROOFED } from "./towerGroups";

/**
 * Ironwood (4,000 Marks) — an ancient living tree grown into a tower (contract tools/art/skins/README.md; big kit, §11).
 *
 * The look: dark bark over the masonry split by glowing amber sap veins, living timber galleries, wrought iron and olive
 * cloth. The stock roof is hidden and a CANOPY grows in its place: gnarled boughs rising from a bark collar into great
 * leaf clumps — deep green, sunlit on top, one turning russet — that rock and breathe in the wind (the skin's own
 * motion: Motion.sway + pulse); over the archer's bow, the poison brew and the beacon's orb the crown stands on four
 * gnarled boughs. Roots grip the plinth, bark strands spiral up the drum under an iron hoop, glowing sap-pods swing
 * from the eaves, small leafy boughs sprout from the walls and wave (Motion.flap), amber fireflies twinkle. Frost and
 * lightning (no roof the kit may hide) and the hex totem's pole wear a leaf wreath. In a level: leaves fall from the
 * crown, and the shots land in a burst of leaves, then amber sap sparks, in turn.
 * Props: art/hq/skins/ironwood/kit.glb (tools/art/skins/ironwood.py).
 */
/** The roof storeys that hold the weapon or the element (bow, brew, the beacon's orb): the crown stands on boughs there. */
const PAVILIONS = ["archer", "poison", "beacon"];
const CROWNED = [...ROOFED, "arcane"].filter((t) => !PAVILIONS.includes(t));
/** The crown spreads this far past the stock roof's widest radius (a tree is wider than the roof it replaces). */
const CANOPY_MARGIN = 0.12;
/** The barracks' roof envelope is its hall plus a turret pole: the crown is fitted to the hall's box (see verdigris.ts). */
const BARRACKS_CANOPY: AttachmentFit = { fitTo: { to: "roof", shape: "box", margin: 0.6 }, scale: 0.65 };
const BEACON_RISE = 0.27;
/** The fire tower's chimney and the beacon's orb cage are tall and narrow: the crown keeps a tree's proportions there, spread wide. */
const NARROW: FitSpec = { to: "roof", height: "keep", margin: 0.9 };
/** The wind in the crown: a slow rock about its foot and a breath in its leaves. */
const WIND = { sway: 2.5, pulse: 0.025, hz: 0.28 } as const;
const WREATH: FitSpec = { to: "gallery", height: "keep", margin: 0.06 };
/** The three sides the game camera sees (wall1 / corner1 are behind the tower): the boughs skip the back. */
const SEEN_WALLS = ["wall0", "wall3"] as const;

export const IRONWOOD: TowerKit = {
  id: "tower.ironwood",
  asset: "skins/ironwood/kit",
  hide: ["roof"],
  hideFit: { hex: [] },
  tier4: true,
  slots: {
    stone: { tint: 0x4a3424, mix: 0.9, gain: 0.8, roughness: 0.92, metalness: 0, keepAccents: false, pattern: { kind: "veins", colour: 0xe39a2a, scale: 4, strength: 0.75, glow: 1.1 } },
    roof: { tint: 0x557f2e, mix: 0.9, gain: 1.1, roughness: 0.9, keepAccents: false, pattern: { kind: "flecks", colour: 0xb5d060, scale: 30, strength: 0.6 } },
    wood: { tint: 0x5c3d22, mix: 0.85, gain: 0.85, roughness: 0.8, pattern: { kind: "grain", colour: 0x2a170a, scale: 10, strength: 0.6 } },
    metal: { tint: 0x37332f, mix: 0.9, metalness: 0.85, roughness: 0.5, keepAccents: false },
    cloth: { tint: 0x5d7a2a, mix: 0.85, roughness: 0.8, keepAccents: false, pattern: { kind: "grain", colour: 0x3c5418, scale: 14, strength: 0.4 } },
  },
  attachments: [
    { piece: "canopy", at: [], towers: CROWNED, ifHidden: "roof", fitTo: { to: "roof", margin: CANOPY_MARGIN }, motion: WIND, fit: { barracks: BARRACKS_CANOPY, fire: { fitTo: NARROW } } },
    { piece: "canopypav", at: [], towers: PAVILIONS, ifHidden: "roof", fitTo: { to: "roof", margin: CANOPY_MARGIN }, motion: WIND, fit: { beacon: { fitTo: NARROW, offset: [0, BEACON_RISE, 0] } } },
    { piece: "wreath", at: [], towers: ["frost", "lightning"], fitTo: WREATH, motion: { pulse: 0.03, hz: 0.3 }, fit: { frost: { offset: [0, 0.24, 0] } } },
    // the hex totem keeps its feather crown: the wreath rings its pole's top under the mask
    { piece: "wreath", at: ["top"], towers: ["hex"], offset: [0, -0.1, 0], scale: [0.36, 0.38, 0.4], motion: { pulse: 0.03, hz: 0.3 } },
    { piece: "bark", at: [], fitTo: { to: "drum", taper: true, margin: 0.02 }, fit: { barracks: { fitTo: { to: "drum", shape: "box", margin: 0.05 } } } },
    { piece: "roots", at: [], fitTo: { to: "plinth", height: "keep" } },
    // sap-pods from every eave (the slender shafts hang them from their upper band), swinging on their stalks
    { piece: "pod", at: ["crest"], offset: [0, -0.01, 0.03], scale: [1.9, 2.1, 2.3], motion: { sway: 12, glow: 0.35, hz: 0.5 }, fit: {
      arcane: { at: ["band"], scale: [1.5, 1.6, 1.7] }, hex: { at: ["band"], scale: [1.5, 1.6, 1.7] },
    } },
    { piece: "bough", at: SEEN_WALLS, offset: [0, 0.08, 0], scale: [1.8, 2.0, 2.2], motion: { flap: { deg: 10, axis: "up", hz: 0.4 } }, fit: {
      arcane: { scale: [1.3, 1.4, 1.5] }, hex: { scale: [1.3, 1.4, 1.5] },
    } },
    { piece: "firefly", at: ["apex"], offset: [0.22, -0.05, 0.12], scale: 1.3, detail: true, motion: { twinkle: 0.3, bob: 0.05, hz: 0.25 }, fit: { arcane: { offset: [0.22, 0.3, 0.1] }, hex: { offset: [0.2, 0.1, 0.1] } } },
    { piece: "firefly", at: ["crest3"], offset: [0.05, 0.25, 0.12], scale: 1.2, detail: true, motion: { twinkle: 0.27, bob: 0.07, spin: 0.6, hz: 0.2 } },
  ],
  idle: { kind: "leaves", colour: 0x6e9a3a, every: [1.4, 2.4], at: "apex", count: 5, radius: 0.3, fit: { beacon: { lift: BEACON_RISE } } },
  // a burst of leaves, then amber sap sparks, in turn
  flourish: {
    impacts: [{ kind: "leafburst", colour: 0x7fa83a, count: 7 }, { kind: "sparks", colour: 0xffb040, count: 6 }],
    impact: { kind: "leafburst", colour: 0x7fa83a, count: 7 },
    muzzle: { colour: 0xffc060, count: 3 },
  },
  preview: { tower: "archer", tier: 3 },
};
